// src/pages/Leaderboard.tsx
import { useQuery } from '@tanstack/react-query';
import { useLeague } from '../context/LeagueContext';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FaArrowUp, FaArrowDown, FaMinus, FaCrown } from 'react-icons/fa';
import { AnimatePresence, motion } from 'framer-motion';
import { Box, CircularProgress } from '@mui/material';
import TeamBreakdownCard from '../components/TeamBreakdownCard';
import './Leaderboard.css';
import { ClutchBreakdown, fetchLeaderboard } from '../services/ClutchService';
import { logEvent }    from 'firebase/analytics';
import { analytics } from '../firebase';

export type ClutchTeam = {
  id: string;
  name: string;
  logo: string;
  color: string;
  clutch: number;
  trend: 'up' | 'down' | 'same';
  breakdown?: {
    last10?: number;
    winStreak?: number;
    runDiff?: number;
    injuries?: number;
    hypeNews?: number;
    antiHype?: number;
    overUnderBonus?: number;
    underdogBoost?: number;
    hypeHits: { headline: string; url: string; source: string }[];
    antiHypeHits: { headline: string; url: string; source: string }[];
    neutralHits: { headline: string; url: string; source: string }[];
  };
};

function normalizeBreakdown(b?: ClutchBreakdown['breakdown']): ClutchBreakdown['breakdown'] {
  return {
    last10: b?.last10 ?? 0,
    winStreak: b?.winStreak ?? 0,
    runDiff: b?.runDiff ?? 0,
    injuries: b?.injuries ?? 0,
    hypeNews: b?.hypeNews ?? 0,
    antiHype: b?.antiHype ?? 0,
    overUnderBonus: b?.overUnderBonus ?? 0,
    underdogBoost: b?.underdogBoost ?? 0,
    eliteBonus: b?.eliteBonus ?? 0,
    homeFieldBonus: b?.homeFieldBonus ?? 0,
    fatiguePenalty: b?.fatiguePenalty ?? 0,
    dominanceBonus: b?.dominanceBonus ?? 0,
    balancedTeamBonus: b?.balancedTeamBonus ?? 0,
    roadWarriorBonus: b?.roadWarriorBonus ?? 0,
    divisionFavoriteBonus: b?.divisionFavoriteBonus ?? 0,
    wildcardChaserBonus: b?.wildcardChaserBonus ?? 0,
    playoffPenalty: b?.playoffPenalty ?? 0,
    playoffStatus: b?.playoffStatus ?? 'eliminated',
    hypeHits: b?.hypeHits ?? [],
    antiHypeHits: b?.antiHypeHits ?? [],
    neutralHits: b?.neutralHits ?? [],
  };
}



export default function Leaderboard() {
  const { league } = useLeague();
  const [sortMode] = useState<'clutch' | 'momentum' | 'hype'>('clutch');
  const [selectedTeam, setSelectedTeam] = useState<ClutchTeam | null>(null);

  useEffect(() => {
    logEvent(analytics, 'view_leaderboard');
  }, []);

  const { data, isLoading } = useQuery<{
    updatedAt: number;
    results: ClutchTeam[];
  }>({
    queryKey: ['leaderboard', league],
    queryFn: () => fetchLeaderboard(league),
    refetchInterval: 10000,
  });
  
  const teams = useMemo(() => data?.results ?? [], [data]);
  const updatedAt = data?.updatedAt ?? null;

  const prevTeamsRef = useRef<ClutchTeam[]>([]);

  const sortedTeams = [...teams].sort((a, b) => {
    switch (sortMode) {
      case 'momentum':
        return (b.breakdown?.last10 ?? 0) + (b.breakdown?.winStreak ?? 0) -
               ((a.breakdown?.last10 ?? 0) + (a.breakdown?.winStreak ?? 0));
      case 'hype':
        return (b.breakdown?.hypeNews ?? 0) + (b.breakdown?.antiHype ?? 0) -
               ((a.breakdown?.hypeNews ?? 0) + (a.breakdown?.antiHype ?? 0));
      default:
        return b.clutch - a.clutch;
    }
  });
  
  
  const ranked = sortedTeams.map((team, i) => {
    const prevRank = prevTeamsRef.current.findIndex((t) => t.id === team.id);
    let trend: 'up' | 'down' | 'same' = 'same';
    let delta = 0;
  
    if (prevRank !== -1) {
      if (prevRank > i) trend = 'up';
      else if (prevRank < i) trend = 'down';
      delta = prevRank - i;
    }
  
    return { ...team, trend, delta };
  });
  
  
  
  useEffect(() => {
    if (teams.length > 0 && prevTeamsRef.current.length === 0) {
      // Initial load – preload previous teams to allow trends on 2nd fetch
      prevTeamsRef.current = [...teams].sort((a, b) => b.clutch - a.clutch);
    } else if (teams.length > 0) {
      // On every valid update
      prevTeamsRef.current = [...teams].sort((a, b) => b.clutch - a.clutch);
    }
  }, [teams]);
  
  

  return (
    <>
    <div className="leaderboard">
      {isLoading && 
        <Box
        p={4}
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="60vh"
        className="loading"
      >
        <CircularProgress />
      </Box>
      }
      <AnimatePresence initial={false}>
        {ranked.map((team, i) => (
          <div key={team.id + i}>
          <motion.div
            layout
            transition={{ duration: 0.3, ease: 'easeOut' }}
            className={`leaderboard-row ${selectedTeam?.id === team.id ? 'open' : ''}`}
            style={{
              background: `linear-gradient(to right, #${team.color}30 8%, #1b1c1f 30%, #1b1c1f 70%)`
            }}
          >
            <div className="clutch-score">
              {team.trend === 'up' && <FaArrowUp className="trend-icon trend-icon--up" />}
              {team.trend === 'down' && <FaArrowDown className="trend-icon trend-icon--down" />}
              {team.trend === 'same' && <FaMinus className="trend-icon" />}
            </div>
            <span className="rank">#{i + 1}</span>
          <div className="team-info"
            onClick={() =>
              setSelectedTeam(prev => (prev?.id === team.id ? null : team))
            }
            >
            <img src={team.logo} alt={team.name} />
            <span className="team-name">{team.name}</span>
            {i === 0 && <FaCrown className="mvp-icon right" title="MVP Team" />}
          </div>
          <div>
            <div className="clutch-score">
            {team.clutch?.toFixed(1) ?? '–'}
            </div>
          </div>
          </motion.div>

          {selectedTeam?.id === team.id && (
          <motion.div
            key={`${team.id}-breakdown`}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            className="breakdown-panel"
            style={{
              background: `linear-gradient(to right, #${team.color}30 8%, #1b1c1f 30%, #1b1c1f 70%)`,
              borderLeft: `8px solid #${team.color}`
            }}
          >
            <TeamBreakdownCard
              updatedAt={updatedAt ? updatedAt : 0}
              teamId={team.id}
              teamName={team.name}
              score={team.clutch}
              breakdown={normalizeBreakdown(team.breakdown)}
              teamLogo={team.logo}
            />
          </motion.div>
        )}

          </div>
        ))}

      </AnimatePresence>

    </div>
    </>
  );
}